// Deciding whether "/" is a marketing page or the tail end of a sign-in.
//
// The site root serves two masters. For a visitor it is the public landing
// page, deliberately rendered with no MSAL and no providers so it loads fast.
// For someone signing in it is ALSO the OAuth redirect URI — MSAL defaults
// redirectUri to window.location.origin, which is exactly "/".
//
// When "/" was added to the public route list the second role was lost: a user
// came back from Microsoft with a valid authorization code in the URL, the app
// rendered the landing page without ever initialising MSAL, and the code was
// dropped on the floor. handleRedirectPromise() never ran, no account was
// cached, and the user was returned to the landing page still signed out —
// every single time. Reported as "click sign in, see the right screen for a
// second, then back to the landing page".
//
// So the root has to look at the URL before it picks which app to render.

// MSAL's own error codes come back on the redirect URI the same way a success
// does, and they need to reach MSAL too — otherwise a failed sign-in is
// silently indistinguishable from never having tried.
const RESPONSE_PARAMS = ['code', 'error', 'id_token', 'access_token'] as const;

function paramsFrom(search: string, hash: string): URLSearchParams[] {
  const out = [new URLSearchParams(search)];
  // msal-browser v3 uses ResponseMode.QUERY, but the fragment form is still
  // valid OAuth and is what implicit-flow and some error paths produce.
  // Checking both costs nothing and means a response can never be missed.
  const fragment = hash.startsWith('#') ? hash.slice(1) : hash;
  if (fragment) out.push(new URLSearchParams(fragment));
  return out;
}

/**
 * Is this page load carrying an OAuth response back from Microsoft?
 *
 * Requires `state` alongside the response parameter. MSAL always sends state
 * (it is how it correlates the response to the request), and requiring it
 * keeps an ordinary marketing link like `/?code=SPRING24` from being mistaken
 * for a sign-in and booting the whole app.
 */
export function hasAuthRedirectResponse(
  { search, hash }: { search: string; hash: string } = window.location,
): boolean {
  return paramsFrom(search, hash).some(
    (p) => p.has('state') && RESPONSE_PARAMS.some((k) => p.has(k)),
  );
}

// Where msal-browser records which accounts it has cached. Verified against
// the bundled library rather than assumed; if a future major version renames
// it, hasCachedMsalAccount() degrades to false — a signed-in user sees the
// landing page and has to click Sign in, which is the old behaviour, not a
// lockout.
const MSAL_ACCOUNT_KEYS = 'msal.account.keys';

/**
 * Does MSAL already have a signed-in account cached?
 *
 * Read directly out of storage because this runs BEFORE msalInstance
 * .initialize(), which is the whole point — we are deciding whether to pay for
 * MSAL at all. Without this a signed-in user who types the bare domain gets
 * the marketing page instead of their dashboard.
 */
export function hasCachedMsalAccount(storage: Pick<Storage, 'getItem'> = localStorage): boolean {
  try {
    const raw = storage.getItem(MSAL_ACCOUNT_KEYS);
    if (!raw) return false;
    const keys: unknown = JSON.parse(raw);
    return Array.isArray(keys) && keys.length > 0;
  } catch {
    // Storage blocked (private mode, cookie policy) or the value is not JSON.
    // Fall back to the landing page — the Sign in button still works.
    return false;
  }
}

/**
 * Should the site root render the public landing page?
 *
 * No when a sign-in response is coming back (MSAL must process it) and no when
 * the visitor is already signed in (send them to their dashboard).
 */
export function shouldRenderLanding(
  location: { search: string; hash: string } = window.location,
  storage: Pick<Storage, 'getItem'> = localStorage,
): boolean {
  return !hasAuthRedirectResponse(location) && !hasCachedMsalAccount(storage);
}
